// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xMergeWithCss } from "@eisinc/App/Widgets/DesignSystem/Utilities";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, PropsWithChildren, useMemo, useState } from "react";
import { css } from "styled-components";

const sectionTitleStyles: { title?: TypographyPresentationProps } = {
    title: {
        variant: "h3",
        css: css`
            width: 100%;
            border-top: 1px solid ${getColor("common.border")};
            padding-top: 8px;
            margin-bottom: 23px;
        `,
    },
};

export const SectionTitle: FC<PropsWithChildren<{ extendedStyles?: { title?: TypographyPresentationProps } }>> = ({
    extendedStyles,
    children,
}) => {
    const [styles] = useState(() => xMergeWithCss(sectionTitleStyles, extendedStyles));
    return <Typography {...styles.title}>{children}</Typography>;
};

export const SectionContainer: FC<PropsWithChildren<{ extendedStyles?: { gridContainer?: GridContainerProps } }>> = ({
    extendedStyles,
    children,
}) => {
    const [styles] = useState(() => xMergeWithCss({ gridContainer: { gap: 22 } }, extendedStyles));
    return <GridContainer {...styles.gridContainer}>{children}</GridContainer>;
};

export const ElementWrapper: FC<PropsWithChildren<{ title: React.ReactNode | string }>> = ({ title, children }) => {
    const styles = useMemo(
        () => ({
            wrapper: {
                gap: 0,
                offsetCss: css`
                    align-items: center;
                `,
            },
            title: {
                size: fontConfig.xSmall.size,
                lineHeight: fontConfig.xSmall.lineHeight,
                css: css`
                    color: ${getColor("text.main")};
                `,
            },
            childrenWrpper: {
                css: css`
                    flex: 1;
                    display: flex;
                `,
            },
        }),
        [],
    );
    return (
        <GridContainer {...styles.wrapper}>
            <GridItem width={3}>
                <Typography {...styles.title}>{title}</Typography>
            </GridItem>
            <GridItem width={9}>{children}</GridItem>
        </GridContainer>
    );
};
